import { useNavigate } from 'react-router-dom'
import { Stack, Title, Group, Button, Card, Text, Timeline, Badge } from '@mantine/core'
import { IconArrowLeft, IconAlertCircle, IconCircleCheck } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { tracesApi } from '@/api'
import { formatDuration } from '@/utils/format'

interface TraceDetailProps {
  traceId: string
}

export function TraceDetail({ traceId }: TraceDetailProps) {
  const navigate = useNavigate()
  
  const { data: trace, isLoading } = useQuery({
    queryKey: ['traces', traceId],
    queryFn: async () => {
      const { data } = await tracesApi.get(traceId)
      return data
    },
  })

  if (isLoading || !trace) {
    return <div>Loading...</div>
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/traces')}
          >
            Back
          </Button>
          <Title order={2}>Trace Details</Title>
        </Group>
        {trace.error_count > 0 ? (
          <Badge color="red" variant="light" size="lg">
            {trace.error_count} errors
          </Badge>
        ) : (
          <Badge color="green" variant="light" size="lg">
            Success
          </Badge>
        )}
      </Group>

      <Card shadow="sm" p="lg" radius="md">
        <Stack gap="md">
          <Group>
            <Text fw={500}>Trace ID:</Text>
            <Text c="dimmed" style={{ fontFamily: 'monospace' }}>{trace.trace_id}</Text>
          </Group>
          <Group>
            <Text fw={500}>Operation:</Text>
            <Text c="dimmed">{trace.root_operation}</Text>
          </Group>
          <Group>
            <Text fw={500}>Duration:</Text>
            <Text c="dimmed">{formatDuration(trace.duration_ms)}</Text>
          </Group>
          <Group>
            <Text fw={500}>Services:</Text>
            <Text c="dimmed">{trace.services.join(', ')}</Text>
          </Group>
        </Stack>
      </Card>

      <Card shadow="sm" p="lg" radius="md">
        <Text size="lg" fw={600} mb="md">
          Span Timeline
        </Text>
        <Timeline active={trace.spans.length} bulletSize={24} lineWidth={2}>
          {trace.spans.map((span) => (
            <Timeline.Item
              key={span.span_id}
              bullet={span.status === 'error' ? <IconAlertCircle size={12} /> : <IconCircleCheck size={12} />}
              color={span.status === 'error' ? 'red' : 'green'}
              title={span.operation_name}
            >
              <Text c="dimmed" size="sm">
                {span.service_name} - {formatDuration(span.duration_ms)}
              </Text>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>
    </Stack>
  )
}